<template>
  <div class="storyblok-page">
    <div v-if="pageDataLoading" class="loader__wrapper">
      <Spinner />
    </div>
    <SFlexLayout v-if="!pageDataLoading && data" :rows="data?.rows" :hero="data?.hero" />
  </div>
</template>
<script lang="ts" setup>
import { useStoryblokStore } from '@/stores/storyblok';
import { createError, useImage, useRoute, useServerSeoMeta, useSeoMeta } from '#imports';
import { storeToRefs } from 'pinia';
import { useStoryblokBridge, useStoryblokApi } from '@storyblok/vue';
import { loadStoryblokBridge } from '@storyblok/js';
import { ROOT_PATH, VARIABLE_ROUTE_TYPE } from '@/resources/helpers';

const img = useImage();

const storyblokApi = useStoryblokApi();
const storyblokStore = useStoryblokStore();
const route = useRoute();
const props = defineProps({
  slug: {
    type: String,
    default: 'homepage',
    required: true
  },
  pageType: {
    type: String,
    required: false,
    default: 'storyblok-page'
  }
});

const SFlexLayout = defineAsyncComponent(() => import('@/components/organisms/SFlexLayout.vue'));
const Spinner = defineAsyncComponent(() => import('@/components/atoms/Spinner.vue'));

const { pageData, pageDataLoading } = storeToRefs(storyblokStore);
const pageDataDev = ref(null);
const data = computed(() => (!pageDataDev.value ? pageData.value?.content : pageDataDev.value?.content));

// preload first image
const firstImage = computed(() => {
  const fImage = pageData.value?.rows?.[0]?.sections?.[0]?.columns?.[0]?.components?.[0];
  if (fImage?.module === 'SGeneralBanner') {
    if (route.meta.isMobile) {
      return img(fImage.background.mobileImage, {
        format: fImage.background.mobileImage?.endsWith('.svg') ? 'svg' : 'webp',
        quality: 80,
        width: fImage.background.mobileImageWidth
      });
    }

    return img(fImage.background.desktopImage, {
      format: fImage.background.desktopImage?.endsWith('.svg') ? 'svg' : 'webp',
      quality: 80,
      width: fImage.background.desktopImageWidth
    });
  }

  return null;
});

function applySeoMeta() {
  if (data.value?.title || data.value?.description) {
    useSeoMeta({
      title: data.value?.title,
      description: data.value?.description
    });
  }
}

onServerPrefetch(async () => {
  if (!route.query?._storyblok) {
    await storyblokStore.fetchPage(props.slug, route.query?._storyblok ? 'draft' : 'published');

    if (pageData.value === null) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found'
      });
    }

    if (firstImage.value) {
      useServerSeoMeta({
        title: data.value?.title,
        link: [
          {
            rel: 'preload',
            href: firstImage.value,
            as: 'image'
          }
        ]
      });
    }
  }
});

if (import.meta.server) {
  applySeoMeta();
}

onMounted(async () => {
  if (route.query?._storyblok) {
    const { data } = await storyblokApi.get(`cdn/stories${route.path}`, {
      version: 'draft'
    });
    pageDataDev.value = data ? storyblokStore.parsePage(data.story) : null;
    loadStoryblokBridge();

    if (pageDataDev.value?.id) {
      useStoryblokBridge(
        parseInt(pageDataDev.value.id),
        (newData) => {
          if (newData?.content) {
            pageDataDev.value = storyblokStore.parsePage(newData);
          }
        },
        {
          preventClicks: true
        }
      );
    }
  }

  const routeSlug = route.path;

  if (routeSlug !== `/${pageData.value?.slug}`) {
    await storyblokStore.fetchPage(props.slug);
  }

  applySeoMeta();
});

watch(
  () => route.params.slug,
  async (newVal, oldVal) => {
    if (import.meta.server) return;
    if (![VARIABLE_ROUTE_TYPE, 'slug'].includes(route.name as string) || pageData.value?.slug === props.slug) {
      return;
    }

    if (newVal !== oldVal) {
      await storyblokStore.fetchPage(props.slug);
    } else if (Array.isArray(newVal) && Array.isArray(oldVal) && newVal.join(ROOT_PATH) !== oldVal.join(ROOT_PATH)) {
    }
  }
);
</script>

<style lang="scss" scoped>
.loader {
  &__wrapper {
    height: calc(100vh - 70px);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100vw;
  }
}

.storyblok-page {
  min-height: 100vh;
  display: flex;
}
</style>
